import { viewerApi } from '@viewer/lib/viewerApi'
import { useAppStore } from '@core/store'
import { isFeatureEnabled } from '@core/features'
import { runTool } from './tools'

export const MCP_SNAPSHOT_FORMAT = 'vn2r-mcp-snapshot'

interface SnapshotElement {
  localId: number
  ifcClass: string
  data: unknown
}

interface SnapshotModel {
  id: string
  filename: string
  sha256: string
  schemaVersion: string | null
  summary: string
  georeferencing: string
  classes: { name: string; count: number }[]
  elements: SnapshotElement[]
}

export interface McpSnapshot {
  format: typeof MCP_SNAPSHOT_FORMAT
  version: 1
  exportedAt: string
  models: SnapshotModel[]
}

/** Build the read-only data package consumed by the local stdio MCP server. */
export async function buildMcpSnapshot(): Promise<McpSnapshot> {
  if (!isFeatureEnabled('mcpServer')) {
    throw new Error('A integração MCP está desativada por configuração.')
  }
  const store = useAppStore.getState()
  const models: SnapshotModel[] = []
  for (const entry of store.models) {
    const model = viewerApi.getModel(entry.id)
    if (!model) continue
    const categories = await model.getItemsOfCategories([/.*/])
    const classes = Object.entries(categories)
      .map(([name, ids]) => ({ name: name.toUpperCase(), count: ids?.length ?? 0 }))
      .filter((row) => row.count > 0)
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    const elements: SnapshotElement[] = []
    for (const [ifcClass, categoryIds] of Object.entries(categories)) {
      const ids = categoryIds ?? []
      for (let offset = 0; offset < ids.length; offset += 250) {
        const batch = ids.slice(offset, offset + 250)
        const data = await model.getItemsData(batch, {
          attributesDefault: true,
          relationsDefault: { attributes: true, relations: false },
        })
        for (let index = 0; index < batch.length; index += 1) {
          elements.push({
            localId: batch[index],
            ifcClass: ifcClass.toUpperCase(),
            data: data[index] ?? null,
          })
        }
      }
    }
    const [summary, georeferencing] = entry.rawIfcAvailable === false
      ? [
          `Modelo Fragments ${entry.fileName}; o IFC de origem não está disponível.`,
          'Georreferenciação disponível apenas nos metadados do Fragments.',
        ]
      : await Promise.all([
          runTool(entry.id, 'resumo_modelo', {}),
          runTool(entry.id, 'georreferenciacao', {}),
        ])
    models.push({
      id: entry.id,
      filename: entry.fileName,
      sha256: entry.sha256,
      schemaVersion: entry.schemaVersion,
      summary,
      georeferencing,
      classes,
      elements,
    })
  }
  return {
    format: MCP_SNAPSHOT_FORMAT,
    version: 1,
    exportedAt: new Date().toISOString(),
    models,
  }
}

export async function downloadMcpSnapshot(): Promise<void> {
  const snapshot = await buildMcpSnapshot()
  const label = snapshot.models.length === 1
    ? snapshot.models[0].filename.replace(/\.(ifc|frag|fragments)$/i, '')
    : 'federacao'
  const blob = new Blob([JSON.stringify(snapshot)], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = `${label}.vn2r-mcp.json`
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
